"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type WorkspaceTabRailActivePosition =
  | "first"
  | "middle"
  | "last"
  | "only"
  | null;

type ContextValue = {
  activePosition: WorkspaceTabRailActivePosition;
  setActivePosition: (next: WorkspaceTabRailActivePosition) => void;
};

const WorkspaceTabRailPositionContext = createContext<ContextValue | null>(null);

export function WorkspaceTabRailPositionProvider({
  children,
  initialPosition = null,
}: {
  children: ReactNode;
  initialPosition?: WorkspaceTabRailActivePosition;
}) {
  const [activePosition, setActivePositionState] =
    useState<WorkspaceTabRailActivePosition>(initialPosition);

  const setActivePosition = useCallback((next: WorkspaceTabRailActivePosition) => {
    setActivePositionState((current) => (current === next ? current : next));
  }, []);

  const value = useMemo<ContextValue>(
    () => ({ activePosition, setActivePosition }),
    [activePosition, setActivePosition],
  );

  return (
    <WorkspaceTabRailPositionContext.Provider value={value}>
      {children}
    </WorkspaceTabRailPositionContext.Provider>
  );
}

export function useWorkspaceTabRailActivePosition(): WorkspaceTabRailActivePosition {
  return useContext(WorkspaceTabRailPositionContext)?.activePosition ?? null;
}

export function useSetWorkspaceTabRailActivePosition(): (
  next: WorkspaceTabRailActivePosition,
) => void {
  const ctx = useContext(WorkspaceTabRailPositionContext);
  return ctx?.setActivePosition ?? noop;
}

function noop() {}
